import { Button, Modal, Panel } from '@sorrem/ui';
import type { ChoiceOption } from './ConfirmModalContext';
import styles from './ConfirmModal.module.scss';

interface ConfirmModalProps {
    /** Controls whether the modal is rendered. */
    isOpen: boolean;
    /** Heading shown at the top of the modal. */
    title: string;
    /** Optional explanatory copy shown under the heading. */
    description?: string;
    /** Label for the affirmative action button. */
    confirmLabel: string;
    /** Label for the dismiss action button. */
    cancelLabel: string;
    /** When set, replaces the single confirm button with one button per choice. */
    choices?: readonly ChoiceOption[];
    /** Called when the user confirms. */
    onConfirm: () => void;
    /** Called with the picked choice's id when `choices` is set. */
    onChoose?: (id: string) => void;
    /** Called when the user cancels or dismisses. */
    onCancel: () => void;
}

/**
 * Shared confirmation modal used for irreversible or important user actions.
 * Escape cancels (handled by `Modal`, which holds focus while open).
 */
export function ConfirmModal({
    isOpen,
    title,
    description,
    confirmLabel,
    cancelLabel,
    choices,
    onConfirm,
    onChoose,
    onCancel,
}: ConfirmModalProps): JSX.Element | null {
    if (!isOpen) {
        return null;
    }

    return (
        <Modal label={title} onClose={onCancel} autoClose={false}>
            <Panel className={styles.confirmModal} frame="thick" glow>
                <h3 className={styles.title}>{title}</h3>
                {description !== undefined && description.trim().length > 0 && (
                    <p className={styles.description}>{description}</p>
                )}
                <div className={styles.actions}>
                    <Button variant="secondary" onClick={onCancel}>{cancelLabel}</Button>
                    {choices === undefined
                        ? <Button onClick={onConfirm}>{confirmLabel}</Button>
                        : choices.map((choice, index) => (
                            <Button
                                key={choice.id}
                                variant={index === choices.length - 1 ? 'primary' : 'secondary'}
                                onClick={() => { onChoose?.(choice.id); }}
                            >
                                {choice.label}
                            </Button>
                        ))}
                </div>
            </Panel>
        </Modal>
    );
}
